import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import React from "react";
import {WithTranslation, withTranslation} from "react-i18next";
import {BsChevronDown, BsChevronRight} from "react-icons/bs";
import {RouteComponentProps, withRouter} from "react-router-dom";
import styled from "styled-components";

import PrimaryButton from "../../common/components/PrimaryButton";
import {getAllActivities, getCurrentUser, getGroupsByPrograms,} from "../../common/requests";
import {routes} from "../../common/routes";
import User from "../../dtos/User";
import {FilterModes, IActivity, ICalendarGroup, IFilter, IFilterMode} from "../calendar/types";
import GroupInformationCollapseView from "./components/GroupInformationCollapseView";
import {Helmet} from "react-helmet";
import {hasGroupCompleted} from "../../utils/qidigo";
import {DEFAULT_IMAGE} from "../../common/constants";
import {GroupOffers} from "../../common/components/GroupOffers";
import queryString from "query-string";
import FilterButtons from "../filterButtons/FilterButtons";

const CollapseViewContainer = styled.div`
  padding: 40px;
`;

enum ACTION {
    ADD_TO_WAITING_LIST,
    ADD_TO_CART,
    NOT_APPLICABLE,
}

const Option = styled.div<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  ${({isExpanded}) => (
    isExpanded
        ? (
            `
          @media only screen and (max-width: 767px) {
            flex-direction: column;
            align-items: center;
            row-gap: 20px;
          }
        `
        )
        : ''
)}
`;

const StyledBsChevronRight = styled(BsChevronRight)`
  flex-shrink: 0;
  margin-left: auto;
  font-size: 40px;
`;

const StyledBsChevronDown = styled(BsChevronDown)`
  flex-shrink: 0;
  margin-left: auto;
  font-size: 40px;
`;

const OptionName = styled.span`
  font-weight: bold;
  font-size: 24px;
`;

const hoverStyles = `
  border-left-color: #27708e;
  box-shadow: 5px 0 #27708e inset;
  transition: box-shadow 0.2s ease-out;
`;

const ActivityContainer = styled.div<{ isOpened: boolean }>`
  padding: 15px 15px 15px 20px;
  border-bottom: 1px solid #eee;
  box-shadow: 0 0 transparent inset;
  transition: box-shadow 0.4s ease-in;
  ${({isOpened}) => (isOpened ? hoverStyles : ``)}
  ${({isOpened}) => (isOpened ? "background-color: #F1F2F3;" : "")}

  &:hover {
    ${hoverStyles}
  }
`;

const ActivityInfo = styled.div`
  align-self: flex-start;
`;

const GroupOption = styled.div`
  display: flex;
  align-items: center;
  margin-top: 15px;
  padding: 15px;
  border-top: 1px solid #eee;

  & + & {
    margin-top: 0;
  }
`;

const GroupControls = styled.div`
  display: flex;
  justify-content: space-between;
  row-gap: 20px;
  column-gap: 20px;

  @media only screen and (max-width: 767px) {
    flex-direction: column;
    align-items: center;
  }
`;

const GroupInformationCollapseViewWrapper = styled.div`
  align-self: flex-start;
`

const GroupDetails = styled.div`
  flex-grow: 1;
`;

const GroupName = styled.div`
  font-size: 20px;
  font-weight: bold;
`;

const ActivityImage = styled.img<{ isExpanded: boolean }>`
  align-self: flex-start;
  margin-right: 20px;
  transition: all 0.2s ease-out;
  height: ${({isExpanded}) => (isExpanded ? 100 : 50)}px;
  width: ${({isExpanded}) => (isExpanded ? 100 : 50)}px;

  object-fit: cover;
`;

const JustifiedText = styled.div`
  text-align: justify;
`;

function getButtonTitle(action: ACTION) {
    switch (action) {
        case ACTION.ADD_TO_CART:
            return "registerToGroup";
        case ACTION.ADD_TO_WAITING_LIST:
            return "offers.addToWaitingList";
        case ACTION.NOT_APPLICABLE:
        default:
            return "complete";
    }
}

function getButtonClassName(action: ACTION) {
    switch (action) {
        case ACTION.ADD_TO_CART:
            return "btn btn-add-to-cart";
        case ACTION.ADD_TO_WAITING_LIST:
            return "btn btn-add-to-waiting-list";
        case ACTION.NOT_APPLICABLE:
        default:
            return "btn disabled";
    }
}

function getNextAction(group: ICalendarGroup) {
    if (!hasGroupCompleted(group)) {
        return ACTION.ADD_TO_CART;
    }

    return group.waiting_list ? ACTION.ADD_TO_WAITING_LIST : ACTION.NOT_APPLICABLE;
}

interface ICollapseViewProps extends RouteComponentProps, WithTranslation {
    programIds: number[],
    organizationId: number,
    organizationSlug: string | undefined,
    shopUrl: string | undefined,
    filterByCategoryIds: number[],
    filterByCategoryMode: IFilterMode,
}

interface ICollapseViewState {
    activities: IActivity[] | undefined,
    selectedActivityId: string | null,
    groups: ICalendarGroup[] | undefined,
    groupId: number | null,
    openedActivities: number[],
    openedGroups: number[],
    isLoading: boolean,
    isLoggedIn: boolean,
    user: Partial<User> | null,
    showGroupOffers: boolean,
    searchParams: string | null,
    categories: IFilter[],
    selectedCategories: IFilter[],
    filteredActivities: IActivity[],
    filteredGroups: ICalendarGroup[],
    filteredCategories: IFilter[],
}

export class CollapseView extends React.Component<ICollapseViewProps,
    ICollapseViewState> {
    state: ICollapseViewState = {
        activities: undefined,
        selectedActivityId: null,
        groups: undefined,
        groupId: null,
        openedActivities: [],
        openedGroups: [],
        user: null,
        isLoading: true,
        isLoggedIn: false,
        showGroupOffers: false,
        searchParams: null,
        categories: [],
        selectedCategories: [],
        filteredActivities: [],
        filteredGroups: [],
        filteredCategories: [],
    };

    async componentDidMount() {
        const user = await getCurrentUser() || null;
        const urlParams = queryString.parse(window.location.search) as any;
        const showGroupOffers = urlParams.groupId !== undefined;

        this.setState({
            user,
            isLoggedIn: Boolean(user),
            isLoading: false,
            groupId: showGroupOffers ? Number(urlParams.groupId) : null,
            showGroupOffers: showGroupOffers,
            searchParams: this.props.location.search,
        });

        return Promise.all([
            getGroupsByPrograms({
                organizationId: this.props.organizationId,
                programIds: this.props.programIds,
            }),
            getAllActivities({organizationId: this.props.organizationId}),
        ])
            .then(([{groups}, {activities}]) => {
                if (this.props.filterByCategoryIds.length > 0) {
                    activities = this.filterActivitiesWithOneOfCategoryIds(activities, this.props.filterByCategoryIds)
                }

                groups = groups.filter(group =>
                    activities.some(activity => group.activity.id === Number(activity.id))
                );

                const categories = this.getCategoriesFromActivities(activities, groups, this.props.filterByCategoryIds, false);
                this.setState({
                    groups,
                    activities: activities,
                    categories: categories,
                    filteredActivities: activities,
                    filteredGroups: groups,
                    filteredCategories: categories,
                });
            })
            .catch((e) => {
                console.error(e);
            });
    }

    filterActivitiesByCategoryIds = (activities : IActivity[], categoryIds : number[]) : IActivity[] => {
        return activities.filter(activity =>
            activity.categories.filter((category) => {
                return categoryIds.find((categoryId) => {
                    return categoryId === Number(category.id);
                })
            }).length === categoryIds.length
        );
    }

    filterActivitiesWithOneOfCategoryIds = (activities : IActivity[], categoryIds : number[]) : IActivity[] => {
        return activities.filter(activity => {
                const category = activity.categories.find(category => {
                    return  categoryIds.includes(Number(category.id));
                });

                return this.props.filterByCategoryMode.mode === FilterModes.in
                    ? category
                    : category === undefined;
        });
    }

  getCategoriesFromActivities(
        activities: IActivity[],
        groups: ICalendarGroup[],
        categoryIdsFilter: number[] = [],
        isFromCategorySelect: boolean
    ): IFilter[] {
        let categories: IFilter[];
        const categoryMap = new Map<number, IFilter>();
        const filterActive = categoryIdsFilter.length > 0;
        const filterModeToApply = isFromCategorySelect
            ? FilterModes.in
            : this.props.filterByCategoryMode.mode

        activities.forEach(activity => {
            const areActivityGroupsVisible = groups.some(group =>
                group.activity.id === Number(activity.id)
            );

            if (areActivityGroupsVisible) {
                activity.categories.forEach(category => {
                    const categoryId = Number(category.id);
                    const notAdded = !categoryMap.has(categoryId);
                    let shouldAdd = filterModeToApply === FilterModes.in
                            ? categoryIdsFilter.includes(categoryId)
                            : !categoryIdsFilter.includes(categoryId)

                    if (notAdded && (!filterActive || shouldAdd)) {
                        categoryMap.set(categoryId, category);
                    }
                });
            }
        });

        categories = Array.from(categoryMap.values());
        categories.sort((a, b) => a.name.localeCompare(b.name));

        return categories;
    }

    goToLogin = () => {
        const redirectUrl = encodeURIComponent(
            `${this.props.location.pathname}${this.state.searchParams}${this.props.location.hash}`
        );
        this.props.history.push(
            `${routes.authentication}/${routes.authenticationSubRoutes.login}?redirect_url=${redirectUrl}`
        );
    };

    getSelectedGroup = () => {
        return (
            this.state.filteredGroups &&
            this.state.filteredGroups.find(
                (x) => String(x.activity.id) === String(this.state.selectedActivityId)
            )
        );
    };

    toggleActivity = (activityId: number) => {
        this.setState({
            openedActivities: this.state.openedActivities.includes(activityId)
                ? []
                : [activityId],
        });
    };

    toggleGroup = (groupId: number) => {
        this.setState({
            openedGroups: this.state.openedGroups.includes(groupId)
                ? this.state.openedGroups.filter((x) => x !== groupId)
                : [...this.state.openedGroups, groupId],
        });
    };

    setSelectedGroup = (groupId: number) => {
        this.updateGroupIdParam(groupId)
        this.setState({
            groupId,
            showGroupOffers: true,
        });
    };

    updateGroupIdParam = (groupId: number | null) => {
        const currentParams = new URLSearchParams(window.location.search);

        groupId
            ? currentParams.set('groupId', String(groupId))
            : currentParams.delete('groupId');

        window.history.pushState({}, '', `${window.location.pathname}?${currentParams.toString()}`);

        this.setState({
            searchParams: `?${currentParams.toString()}`
        })
    };

    handleContinueShopping = () => {
        this.updateGroupIdParam(null);

        this.setState({
            groupId: null,
            showGroupOffers: false,
        })
    }

    onSelectActivityCategory = (newSelectedCategories: IFilter[]) => {
        const {activities = [], groups = [], categories} = this.state;

        if (newSelectedCategories.length === 0) {
            this.setState({
                selectedCategories: newSelectedCategories,
                filteredActivities: activities,
                filteredGroups: groups,
                filteredCategories: categories,
            });
            return;
        }

        let activitiesToKeep = this.filterActivitiesByCategoryIds(
            activities,
            newSelectedCategories.map((category) => Number(category.id))
        )

        const filteredActivities = activities.filter((activity) => {
            return activitiesToKeep.find((activityToKeep) => activity.id === activityToKeep.id)
        })

        const filteredGroups = groups.filter(group =>
            filteredActivities.some(activity => group.activity.id === Number(activity.id))
        );

        const categoryIds = this.state.categories.map((category) => Number(category.id))

        const filteredCategories = this.getCategoriesFromActivities(filteredActivities, filteredGroups, categoryIds, true);

        this.setState({
            selectedCategories: newSelectedCategories,
            filteredActivities: filteredActivities,
            filteredGroups: filteredGroups,
            filteredCategories: filteredCategories,
        })
    }

    render() {
        const {t} = this.props;
        const options: Record<number, ICalendarGroup[]> = sortBy(
            groupBy(this.state.filteredGroups, (x) => x.activity.id),
            (x) => x[0].activity.name
        );

        const selectedGroup =
            this.state.filteredGroups &&
            this.state.filteredGroups.find(
                (x) => String(x.id) === String(this.state.groupId)
            );

        const activityMap = new Map<String, IActivity>();
        this.state.filteredActivities.map((activity: IActivity) => activityMap.set(String(activity.id), activity))
        const selectedActivity = activityMap.get(String(selectedGroup && selectedGroup.activity.id));
        const styleClasses = (
            selectedGroup && selectedActivity && [
                `group-${selectedGroup.id}`,
                ...selectedActivity.categories.map(({id}) => `category-${id}`),
                `program-${selectedGroup.programmation_id}`,
                `activity-${selectedActivity.id}`
            ]
        ) || [];

        return (
            <CollapseViewContainer className={"activity-list-view container"}>
                {
                    this.state.showGroupOffers && selectedActivity && selectedGroup
                        ?
                        <div className={`group-view ${styleClasses.join(" ")} container`}>
                            <GroupOffers
                                onContinueShopping={this.handleContinueShopping}
                                activity={selectedActivity}
                                group={selectedGroup}
                                closeButton={false}
                                shouldRedirectToLogin={false}
                                shopUrl={this.props.shopUrl}
                                goToLogin={this.goToLogin}
                                organizationSlug={this.props.organizationSlug}
                                user={this.state.user}
                            />
                        </div>
                        : <>
                            <Helmet>
                                <title>Collapse view</title>
                            </Helmet>
                            <FilterButtons
                                onSelectionChange={this.onSelectActivityCategory}
                                filters={this.state.filteredCategories}
                                selectedFilters={this.state.selectedCategories}
                            />
                            {!this.state.filteredActivities && <div>{t("offers.loading")}</div>}
                            {this.state.filteredActivities &&
                                Object.entries(options).map(([activityId, groups]) => {
                                    const activity = groups[0].activity;
                                    const isExpanded = this.state.openedActivities.includes(
                                        Number(activityId)
                                    );

                                    const activityDetail = activityMap.get(String(activity.id));
                                    const styleClasses = (
                                        activityDetail && [
                                            `activity-${activityDetail.id}`,
                                            ...activityDetail.categories.map(({id}) => `category-${id}`),
                                        ]
                                    ) || [];

                                    return (
                                        <ActivityContainer
                                            className={"activity " + styleClasses.join(" ")}
                                            key={activityId}
                                            isOpened={isExpanded}
                                        >
                                            <Option
                                                isExpanded={isExpanded}
                                                onClick={() => this.toggleActivity(Number(activityId))}
                                            >
                                                <ActivityImage
                                                    className={"thumbnail"}
                                                    isExpanded={isExpanded}
                                                    src={activity?.image_icon?.path || DEFAULT_IMAGE}
                                                />
                                                <ActivityInfo>
                                                    <OptionName className={"title"}>
                                                        {activity.name}
                                                    </OptionName>
                                                    {isExpanded && (
                                                        <JustifiedText
                                                            dangerouslySetInnerHTML={{
                                                                __html: activity.description,
                                                            }}
                                                        ></JustifiedText>
                                                    )}
                                                </ActivityInfo>
                                                {this.state.openedActivities.includes(Number(activityId)) ? (
                                                    <StyledBsChevronDown/>
                                                ) : (
                                                    <StyledBsChevronRight/>
                                                )}
                                            </Option>
                                            {this.state.openedActivities.includes(Number(activityId)) &&
                                                groups.map((group) => {
                                                    const styleClasses = [
                                                        `group-${group.id}`,
                                                        `program-${group.programmation_id}`
                                                    ];

                                                    const nextAction = getNextAction(group);

                                                    return (
                                                        <GroupOption
                                                            className={"group " + styleClasses.join(" ")}
                                                            key={group.id}
                                                        >
                                                            <GroupDetails>
                                                                <GroupName className={"name"}>{group.name}</GroupName>
                                                                <JustifiedText className={"description"}
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: group.description,
                                                                    }}
                                                                ></JustifiedText>
                                                                <GroupControls>
                                                                    <GroupInformationCollapseViewWrapper>
                                                                        <GroupInformationCollapseView
                                                                            group={group}
                                                                            withLocation
                                                                        />
                                                                    </GroupInformationCollapseViewWrapper>
                                                                    <PrimaryButton
                                                                        className={getButtonClassName(nextAction)}
                                                                        onClick={() => this.setSelectedGroup(group.id)}
                                                                        text={t(getButtonTitle(nextAction))}
                                                                        disabled={nextAction === ACTION.NOT_APPLICABLE}
                                                                    />
                                                                </GroupControls>
                                                            </GroupDetails>
                                                        </GroupOption>
                                                    );
                                                })}
                                        </ActivityContainer>
                                    );
                                })}
                        </>
                }
            </CollapseViewContainer>
        );
    }
}

export default withRouter(withTranslation()(CollapseView));
